import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  ButtonGroup,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { Edit } from '@mui/icons-material';

import { RouterOutput, trpc } from '../../api/trpc';
import { ControlBox } from '../../atoms/control-box';
import { Row } from '../../atoms/row';
import { DialogClose } from '../../atoms/dialog-close';
import { FormTextField } from '../../atoms/form/form-text-field';

type GetData = {
  data?: RouterOutput['network']['get'];
  isLoading: boolean;
  isError: boolean;
};

type Props = {
  getData: GetData;
  canEditStatic?: boolean;
};

export const NetworkWan = ({ getData, canEditStatic = true }: Props) => {
  const [dialogOpen, setDialogOpen] = useState(false);

  const type = getData.data?.static ? 'Static' : 'DHCP';
  const address =
    getData.data?.static?.address ?? getData.data?.wanAddress ?? '---';

  return (
    <>
      <ControlBox
        title={
          <FormattedMessage
            id="settings.service.network.wan"
            defaultMessage="WAN adresa"
          />
        }
        control={
          <Row gap={0.5}>
            <Chip label={type} size="small" />
            <Typography>{address}</Typography>
            {canEditStatic && (
              <IconButton size="small" onClick={() => setDialogOpen(true)}>
                <Edit />
              </IconButton>
            )}
          </Row>
        }
        fullWidth
      />

      {canEditStatic && Boolean(getData.data) && (
        <NetworkWanDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          getData={getData}
        />
      )}
    </>
  );
};

type DialogProps = {
  open: boolean;
  onClose: () => void;
  getData?: GetData;
};

const schema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('dhcp'),
  }),
  z.object({
    type: z.literal('static'),
    address: z.string().ip(),
    netmask: z.string().ip(),
    gw: z.string().ip(),
    dns: z.string().ip(),
  }),
]);

type Schema = z.infer<typeof schema>;

const NetworkWanDialog = ({ open, onClose, getData }: DialogProps) => {
  const { mutate, isLoading } = trpc.network.setWan.useMutation({
    onSuccess: () => {
      onClose();
    },
    onError: (error) => {
      alert('Failed to save WAN settings');
    },
  });

  const onSubmit = (data: Schema) => {
    mutate(data);
  };

  const form = useForm<Schema>({
    defaultValues: {
      type: getData?.data?.static ? 'static' : 'dhcp',
      address: getData?.data?.static?.address ?? '',
      netmask: getData?.data?.static?.netmask ?? '',
      gw: getData?.data?.static?.gw ?? '',
      dns: getData?.data?.static?.dns ?? '',
    },
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    if (open) {
      form.reset({
        type: getData?.data?.static ? 'static' : 'dhcp',
        address: getData?.data?.static?.address ?? '',
        netmask: getData?.data?.static?.netmask ?? '',
        gw: getData?.data?.static?.gw ?? '',
        dns: getData?.data?.static?.dns ?? '',
      });
    }
  }, [open]);

  const type = form.watch('type');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogClose onClose={onClose} />
      <DialogTitle variant="h3">
        <FormattedMessage
          id="network_wan_edit.dialog.title"
          defaultMessage="Editace WAN adresy"
        />
      </DialogTitle>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <DialogContent>
            <Stack spacing={2}>
              <ButtonGroup fullWidth>
                <Button
                  variant={type === 'dhcp' ? 'contained' : 'outlined'}
                  onClick={() => form.setValue('type', 'dhcp')}
                >
                  DHCP
                </Button>
                <Button
                  variant={type === 'static' ? 'contained' : 'outlined'}
                  onClick={() => form.setValue('type', 'static')}
                >
                  Static
                </Button>
              </ButtonGroup>

              {type === 'static' && (
                <>
                  <FormTextField
                    name="address"
                    label={
                      <FormattedMessage
                        id="network_wan_edit.dialog.address"
                        defaultMessage="IP adresa"
                      />
                    }
                    size="small"
                    disabled={isLoading}
                  />

                  <FormTextField
                    name="netmask"
                    label={
                      <FormattedMessage
                        id="network_wan_edit.dialog.netmask"
                        defaultMessage="Maska sítě"
                      />
                    }
                    size="small"
                    disabled={isLoading}
                  />

                  <FormTextField
                    name="gw"
                    label={
                      <FormattedMessage
                        id="network_wan_edit.dialog.gateway"
                        defaultMessage="Výchozí brána"
                      />
                    }
                    size="small"
                    disabled={isLoading}
                  />

                  <FormTextField
                    name="dns"
                    label={
                      <FormattedMessage
                        id="network_wan_edit.dialog.dns"
                        defaultMessage="DNS server"
                      />
                    }
                    size="small"
                    disabled={isLoading}
                  />
                </>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} disabled={isLoading}>
              <FormattedMessage
                id="network_wan_edit.dialog.close"
                defaultMessage="Zavřít"
              />
            </Button>
            <Button variant="contained" type="submit" disabled={isLoading}>
              <FormattedMessage
                id="network_wan_edit.dialog.save"
                defaultMessage="Uložit"
              />
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};
